import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Camera, Trash2, User, Loader2 } from 'lucide-react'
import './ProfileAvatar.css'

/**
 * Profile Avatar Component
 *
 * Displays user avatar with upload/remove functionality.
 * Features hover overlay for camera icon and remove button.
 *
 * @param {Object} props - Component props
 * @param {string} props.src - Current avatar URL
 * @param {string} props.name - User's full name (for initials fallback)
 * @param {Function} props.onUpload - Callback when file is selected
 * @param {Function} props.onRemove - Callback to remove avatar
 * @param {boolean} props.loading - Loading state during upload
 * @param {boolean} props.disabled - Disable interactions
 * @param {string} props.size - Avatar size (small, medium, large)
 */
const ProfileAvatar = ({
	src,
	name = '',
	onUpload,
	onRemove,
	loading = false,
	disabled = false,
	size = 'large',
}) => {
	const [isHovered, setIsHovered] = useState(false)
	const fileInputRef = useRef(null)

	// Generate initials from name
	const getInitials = () => {
		if (!name) return '?'
		const parts = name.trim().split(' ')
		if (parts.length >= 2) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
		}
		return name.substring(0, 2).toUpperCase()
	}

	const handleFileSelect = (e) => {
		const file = e.target.files?.[0]
		if (file && onUpload) {
			onUpload(file)
		}
		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const handleUploadClick = () => {
		if (!disabled && fileInputRef.current) {
			fileInputRef.current.click()
		}
	}

	const handleRemoveClick = (e) => {
		e.stopPropagation()
		if (!disabled && onRemove) {
			onRemove()
		}
	}

	const baseClass = 'profile-avatar'
	const classes = [
		baseClass,
		`${baseClass}--${size}`,
		disabled && `${baseClass}--disabled`,
		loading && `${baseClass}--loading`,
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div
			className={classes}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* Avatar Image or Initials */}
			<div className={`${baseClass}__image-container`}>
				{src ? (
					<img src={src} alt={name || 'Profile'} className={`${baseClass}__image`} />
				) : (
					<div className={`${baseClass}__initials`}>
						{getInitials()}
					</div>
				)}

				{/* Loading Overlay */}
				{loading && (
					<div className={`${baseClass}__loading-overlay`}>
						<Loader2 className={`${baseClass}__spinner`} size={24} />
					</div>
				)}

				{/* Hover Overlay */}
				{!disabled && !loading && isHovered && (
					<div className={`${baseClass}__hover-overlay`} onClick={handleUploadClick}>
						<Camera size={24} className={`${baseClass}__camera-icon`} />
						<span className={`${baseClass}__hover-text`}>
							{src ? 'Change' : 'Upload'}
						</span>
					</div>
				)}
			</div>

			{/* Remove Button */}
			{src && !disabled && !loading && (
				<button
					type="button"
					className={`${baseClass}__remove-btn`}
					onClick={handleRemoveClick}
					title="Remove avatar"
					aria-label="Remove avatar"
				>
					<Trash2 size={14} />
				</button>
			)}

			{/* Hidden File Input */}
			<input
				type="file"
				ref={fileInputRef}
				className={`${baseClass}__file-input`}
				accept="image/jpeg,image/png,image/gif,image/webp"
				onChange={handleFileSelect}
				disabled={disabled || loading}
			/>
		</div>
	)
}

ProfileAvatar.propTypes = {
	src: PropTypes.string,
	name: PropTypes.string,
	onUpload: PropTypes.func,
	onRemove: PropTypes.func,
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large']),
}

export default ProfileAvatar
